import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { AppLayout } from "@/components/layout/AppLayout";
import { usePlanGate } from "@/hooks/usePlanGate";
import { useLanguage } from "@/hooks/useLanguage";
import { useOrganization } from "@/hooks/useOrganization";
import { featurePageCopy, notOwnerNote } from "@/lib/billing/entitlements";
import type { FeatureId } from "@/lib/pricing/plans";

/**
 * A route guard for a Firma page (budget, analytics, private mode, new
 * invoices): on a plan without the feature, or with no plan at all
 * (read-only: these pages write), it shows one card with the way to the plans
 * instead of the page. A downgrade keeps the data; only this view goes.
 * Private mode follows the person's best plan, not the selected company.
 * Open while the gates are off (billing hidden, or not enforced).
 */
export function RequireFeature({ feature, children }: { feature: FeatureId; children: ReactNode }) {
  const { featureBlock } = usePlanGate();
  const block = featureBlock(feature);
  if (!block) return <>{children}</>;
  return (
    <AppLayout>
      <FeatureLocked feature={feature} readOnly={block.error === "read_only"} />
    </AppLayout>
  );
}

export function FeatureLocked({
  feature,
  readOnly = false,
}: {
  feature: FeatureId;
  readOnly?: boolean;
}) {
  const { language } = useLanguage();
  const locale = language === "en" ? "en" : "cs";
  const { currentOrganization } = useOrganization();
  const copy = featurePageCopy(feature, locale, readOnly);
  const isOwner = currentOrganization?.role === "owner";

  return (
    <Card className="mx-auto mt-6 max-w-xl">
      <CardHeader>
        <CardTitle>{copy.title}</CardTitle>
        <CardDescription>{copy.body}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3 px-5 pb-5 pt-0">
        {isOwner ? (
          <Button asChild>
            <LocalizedLink to="/settings/billing">{copy.cta}</LocalizedLink>
          </Button>
        ) : (
          <p className="text-[13px] text-muted-foreground">{notOwnerNote(locale)}</p>
        )}
      </CardContent>
    </Card>
  );
}
